import { useForm, Controller } from "react-hook-form";
import { Input } from "@/components/base/input/input";
import { NativeSelect } from "@/components/base/select";
import { TextArea } from "@/components/base/textarea";
import { Button } from "@/components/base/buttons/button";
import type { CreateVehiclePayload } from "@/api/vehicles";
import type { CustomerDto } from "@/types/dto";

interface FormValues {
  customerId: string;
  make: string;
  model: string;
  year: string;
  licensePlate: string;
  vin: string;
  color: string;
  mileage: string;
  engineNumber: string;
  registrationDate: string;
  notes: string;
}

interface VehicleFormProps {
  customers: CustomerDto[];
  defaultValues?: Partial<{
    customerId: string | null;
    make: string;
    model: string;
    year: number;
    licensePlate: string;
    vin: string | null;
    color: string | null;
    mileage: number | null;
    engineNumber: string | null;
    registrationDate: string | null;
    notes: string | null;
  }>;
  onSubmit: (data: CreateVehiclePayload) => void;
  onCancel: () => void;
  isLoading: boolean;
}

export default function VehicleForm({
  customers,
  defaultValues,
  onSubmit,
  onCancel,
  isLoading,
}: VehicleFormProps) {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      customerId: defaultValues?.customerId ?? "",
      make: defaultValues?.make ?? "",
      model: defaultValues?.model ?? "",
      year: defaultValues?.year?.toString() ?? new Date().getFullYear().toString(),
      licensePlate: defaultValues?.licensePlate ?? "",
      vin: defaultValues?.vin ?? "",
      color: defaultValues?.color ?? "",
      mileage: defaultValues?.mileage?.toString() ?? "",
      engineNumber: defaultValues?.engineNumber ?? "",
      registrationDate: defaultValues?.registrationDate ?? "",
      notes: defaultValues?.notes ?? "",
    },
  });

  const customerOptions = [
    { label: "No owner", value: "" },
    ...customers.map((c) => ({
      label: `${c.firstName} ${c.lastName}`,
      value: c.id,
    })),
  ];

  const onFormSubmit = (values: FormValues) => {
    onSubmit({
      customerId: values.customerId || null,
      make: values.make,
      model: values.model,
      year: parseInt(values.year, 10),
      licensePlate: values.licensePlate,
      vin: values.vin || null,
      color: values.color || null,
      mileage: values.mileage ? parseInt(values.mileage, 10) : null,
      engineNumber: values.engineNumber || null,
      registrationDate: values.registrationDate || null,
      notes: values.notes || null,
    });
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
      <Controller
        name="customerId"
        control={control}
        render={({ field }) => (
          <NativeSelect
            label="Owner (Customer)"
            options={customerOptions}
            value={field.value}
            onChange={field.onChange}
          />
        )}
      />

      <div className="grid grid-cols-2 gap-4">
        <Controller
          name="make"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Input
              label="Make"
              placeholder="Toyota"
              isRequired
              isInvalid={!!errors.make}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
        <Controller
          name="model"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Input
              label="Model"
              placeholder="Camry"
              isRequired
              isInvalid={!!errors.model}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Controller
          name="year"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Input
              label="Year"
              placeholder="2024"
              type="number"
              isRequired
              isInvalid={!!errors.year}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
        <Controller
          name="color"
          control={control}
          render={({ field }) => (
            <Input
              label="Color"
              placeholder="Silver"
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Controller
          name="licensePlate"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Input
              label="Reg No (License Plate)"
              placeholder="IL-ABC123"
              isRequired
              isInvalid={!!errors.licensePlate}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
        <Controller
          name="registrationDate"
          control={control}
          render={({ field }) => (
            <Input
              label="Date of Registration"
              type="date"
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Controller
          name="vin"
          control={control}
          render={({ field }) => (
            <Input
              label="Chassis No (VIN)"
              placeholder="Optional"
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
        <Controller
          name="engineNumber"
          control={control}
          render={({ field }) => (
            <Input
              label="Engine No"
              placeholder="Optional"
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </div>

      <Controller
        name="mileage"
        control={control}
        render={({ field }) => (
          <Input
            label="Mileage (Km)"
            placeholder="0"
            type="number"
            value={field.value}
            onChange={field.onChange}
          />
        )}
      />

      <Controller
        name="notes"
        control={control}
        render={({ field }) => (
          <TextArea
            label="Notes"
            placeholder="Optional notes..."
            rows={3}
            value={field.value}
            onChange={field.onChange}
          />
        )}
      />

      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" color="secondary" size="md" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="submit"
          color="primary"
          size="md"
          isLoading={isLoading}
          isDisabled={isLoading}
        >
          {defaultValues ? "Save Changes" : "Add Vehicle"}
        </Button>
      </div>
    </form>
  );
}
